import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Groupper, Header, Icon, Modal, Table, Toast, Toolbar } from 'react-frontier';
import { InventoryItem, InventoryItemStatus, InventoryType, SetLoading } from '../SiteClasses';
import { CreateInventoryItemModal, SearchTypeModal } from '../components';
import { useBreadcrumbs, useUser } from '../hooks';
import classNames from 'classnames';
import UserAccess from '../UserAccess';
import moment from 'moment';
import API from '../API';

enum Modals{
	EDIT = 1,
	DELETE = 2,
	SEARCH_TYPE = 3,
}

var ITEM_STATUS: Record<InventoryItemStatus, { label: string, color: string | null }> = {
	[InventoryItemStatus.AVAILABLE]: { label: 'Disponible', color: 'green' },
	[InventoryItemStatus.PENDING]: { label: 'Pendiente', color: null },
	[InventoryItemStatus.SHIPPED]: { label: 'Enviado', color: 'blue' },
	[InventoryItemStatus.DELIVERED]: { label: 'Entregado', color: 'teal' },
	[InventoryItemStatus.DELETED]: { label: 'Eliminado', color: 'red' },
};

var InventoryItemView = ()=>{
	var { itemId } = useParams();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [item, setItem] = useState<InventoryItem>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);

	useEffect(()=>{
		get();
	}, [itemId]);

	var get = ()=>{
		API.getInventoryItem(parseInt(itemId), false, true).then(res=>{
			if(res.error) return setLoadError(res.message);
			setItem(res.data);
			setTitle(`Equipo #${res.data.item_id}`);
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando el equipo.');
		});
	};

	var closeModal = ()=>{
		setShownModal(null);
		setModalPrompts([]);
	};

	var onItemUpdated = (updated: InventoryItem)=>{
		setItem(prev => ({ ...prev, ...updated }));
		closeModal();
	};

	var onTypeSelected = (type: InventoryType, setLoading: SetLoading)=>{
		setLoading(true);
		API.editInventoryItem(parseInt(itemId), { type_id: type.type_id }).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Tipo de equipo actualizado.');
			setItem(prev => ({
				...prev,
				type_id: type.type_id,
				type_name: type.type_name,
				type_icon: type.type_icon,
				consumable: type.consumable,
			}));
			closeModal();
		}).catch(()=>{
			Toast.error('Hubo un error inesperado actualizando el tipo del equipo.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onDelete = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteInventoryItem(parseInt(itemId), true).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Equipo eliminado correctamente.');
			setItem(prev => ({ ...prev, deleted: 1, status: InventoryItemStatus.DELETED }));
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado eliminando el equipo.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!item) return <Header loading text='Cargando' />;

	return <>
		<Groupper
		title={`Equipo #${item.item_id}`}
		fitted
		style={{ marginTop: 20 }}
		width={400}
	>
		{!item.deleted && (hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY) || hasAccess(UserAccess.INVENTORY.DELETE_INVENTORY)) && (
			<Toolbar stretch>
				{hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY) && <Toolbar.Item text='Editar' iconName='pen' onClick={()=> setShownModal(Modals.EDIT)} />}
				{hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY) && <Toolbar.Item text='Tipo' iconName='shuffle' onClick={()=> setShownModal(Modals.SEARCH_TYPE)} />}
				{hasAccess(UserAccess.INVENTORY.DELETE_INVENTORY) && <Toolbar.Item text='Eliminar' iconName='trash' onClick={()=> setShownModal(Modals.DELETE)} />}
			</Toolbar>
		)}
		<Table
			details
			striped
			fitted
			data={[
				['ID', item.item_id],
				['Tipo', item.type_name],
				['Nombre', item.item_name || <Icon name='minus' />],
				['Creador', item.user_creator_name || <Icon name='minus' />],
				['Creación', item.date_created ? moment.unix(item.date_created).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
				['Modelo', item.model || <Icon name='minus' />],
				['Serie', item.serial_number || <Icon name='minus' />],
				['Proveedor', item.seller_name || <Icon name='minus' />],
				['Fecha de compra', item.date_purchase ? moment.unix(item.date_purchase).format('DD/MMM/YY') : <Icon name='minus' />],
				['Estatus', item.status ? <div className={classNames('fr label', ITEM_STATUS[item.status].color)}>{ITEM_STATUS[item.status].label}</div> : <Icon name='minus' />],
				['Comentarios', item.description || <Icon name='minus' />],
			]}
		/>
		<CreateInventoryItemModal
			open={shownModal === Modals.EDIT}
			onClose={closeModal}
			item_id={item.item_id}
			onUpdate={onItemUpdated}
		/>
		<SearchTypeModal
			open={shownModal === Modals.SEARCH_TYPE}
			onClose={closeModal}
			onSelected={onTypeSelected}
			exclude={[item.type_id]}
		/>
		<Modal open={shownModal === Modals.DELETE} onClose={closeModal} size='mini'>
			<Modal.Header>Eliminar equipo</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar este equipo?' subtext='Esta acción no se puede deshacer.' />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={closeModal} />
				<Button text='Eliminar' color='red' onClick={onDelete} />
			</Modal.Actions>
		</Modal>
	</Groupper>
	<Table
		title='Logs'
		striped
		fitted
		headers={['ID', 'Usuario', 'Destino', 'Comentarios', 'Envío', 'Fecha']}
		collapsingIndexes={[0, 1, 4, 5]}
		style={{ margin: 'auto', marginTop: 15, width: 920, maxWidth: '100%' }}
	>
		{!item.logs || item.logs.length === 0 ? (
			<Table.Cell row className='empty' colSpan={6}>
				Sin registros en el historial
			</Table.Cell>
		) : item.logs.map(log=>(
			<Table.Row
				key={log.log_id}
				collapsingIndexes={[0, 1, 4, 5]}
				centeredIndexes={[4]}
				data={[
					log.log_id,
					log.user_creator_name,
					log.warehouse_name
						? <><Icon name='warehouse' /> {log.warehouse_name}</>
						: log.location?.city
						? <><Icon name='location-dot' /> {log.location.city}</>
						: <Icon name='minus' />,
					log.shipment_description
						? <div style={{ maxWidth: 220, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{log.shipment_description}</div>
						: <Icon name='minus' />,
					log.shipment_id
						? <Link style={{ padding: 0, textDecoration: 'underline' }} to={`/shipments/${log.shipment_id}`} target='_blank' rel='noopener noreferrer'>
							{log.shipment_id}
						</Link>
						: <Icon name='minus' />,
					moment.unix(log.date_created).format('DD/MMM/YY HH:mm'),
				]}
			/>
		))}
	</Table>
	</>;
};

export default InventoryItemView;
